<template>
  <div class="autobookpro-page-content">
    <div class="wrapper">
      <h2 class="title">Lead Reselling Dashboard</h2>
      <div class="content">
        <div class="referrer-list">
          <h3 class="sub-title">Referrer List</h3>
          <table class="table table-striped" id="referrer-tbl">
            <thead>
              <th>Name</th>
              <th>On/Off</th>
              <th>Status to send</th>
            </thead>
            <tbody>
              <tr v-for="(referrer, index) in referrers" v-bind:key="index">
                <td class="text-left">{{ referrer.name }}</td>
                <td>
                  <app-checkbox-input
                    class="q-mr-sm"
                    :value="selectedReferrers.includes(referrer.id)"
                    @input="handleChangeSelectReferrer(referrer.id, $event)"
                  ></app-checkbox-input>
                </td>
                <td class="text-right">
                  <q-select
                    style="width: 100%; min-width: 150px"
                    dense
                    label="Status"
                    label-color="white"
                    bg-color="grey-6"
                    square
                    filled
                    map-options
                    :value="referrerStatuses[referrer.id]"
                    :options="statusOptions"
                    :loading="loadingStatus"
                    option-label="name"
                    option-value="id"
                    @input="handleChangeReferrerStatus(referrer.id, $event)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="sent-history">
          <div style="max-width: 250px">
            <p style="text-align: center">Number of leads sent</p>
            <div style="display: flex; justify-content: space-between">
              <div class="week">
                <p>This week</p>
              </div>
              <div class="month">
                <p>Month</p>
              </div>
              <div class="ytd">
                <p>YTD</p>
              </div>
              <div class="all">
                <p>All time</p>
              </div>
            </div>
            <div
              style="display: flex; justify-content: space-between"
              v-if="!loadingQuoteResaleActivitySummary"
            >
              <div class="week">
                <p>{{ quoteResaleActivitySummary.week }}</p>
              </div>
              <div class="month">
                <p>{{ quoteResaleActivitySummary.month }}</p>
              </div>
              <div class="ytd">
                <p>{{ quoteResaleActivitySummary.year }}</p>
              </div>
              <div class="all">
                <p>{{ quoteResaleActivitySummary.all }}</p>
              </div>
            </div>

            <div>
              <table class="table table-striped">
                <thead>
                  <th>Quote Id</th>
                  <th>Quote Buyer Name</th>
                </thead>
                <tbody>
                  <tr
                    v-for="(activity, index) in quoteResaleActivities"
                    v-bind:key="index"
                  >
                    <td class="text-left">{{ activity.quoteId }}</td>
                    <td class="text-left">{{ activity.quoteBuyerName }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="config">
          <div class="row">
            <div class="col-3">
              <p class="config-title">Ship date</p>
            </div>
            <div class="col-3">
              <p class="config-title">ASAP</p>
            </div>
            <div class="col-3">
              <p class="config-title">{{ '< 7days' }}</p>
            </div>
            <div class="col-3">
              <p class="config-title">>= 7days</p>
            </div>
          </div>
          <div class="row">
            <div class="col-3">
              <p
                class="config-title"
                style="margin-top: 10px; font-weight: 700"
              >
                Hours to send
              </p>
            </div>
            <div class="col-3">
              <q-radio left-label v-model="hoursToSend" val="asap" />
            </div>
            <div class="col-3">
              <q-radio left-label v-model="hoursToSend" val="l7" />
            </div>
            <div class="col-3">
              <q-radio left-label v-model="hoursToSend" val="ge7" />
            </div>
          </div>
          <div class="save-wrapper">
            <div>
              <q-btn color="secondary" label="Save" @click="handleSave()" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import graphql from '@client/graphql';
import runPromiseSerial from '@server/lib/promise-serial';
import { sanitizeObject } from '@client/utils/object-helpers';
import AppCheckboxInput from '@client/components/form/checkbox-input';

export default {
  metaInfo: {
    title: 'AutoBook Pro - Lead Reselling Dashboard'
  },
  components: {
    AppCheckboxInput
  },
  apollo: {
    referrers: {
      query: graphql.queries.getReferrers,
      watchLoading(isLoading) {
        this.loading = isLoading;
      },
      error(error) {
        this.error = error;
      },
      result(result) {
        if (result.loading) return;
        this.referrals = result.data.referrals;
      }
    },
    parentStatuses: {
      query: graphql.queries.getParentStatus,
      variables() {
        return { type: 'QUOTE' };
      },
      watchLoading(isLoading) {
        this.loadingStatus = isLoading;
      },
      result(result) {
        if (result.loading) return;
        this.statusOptions = result.data.parentStatuses;
      }
    },
    quoteResellRules: {
      query: graphql.queries.getQuoteResellRules,
      watchLoading(isLoading) {
        this.loadingQuoteResellRules = isLoading;
      },
      result(result) {
        if (result.loading) return;
        this.quoteResellRules = result.data.quoteResellRules;

        // set values
        this.quoteResellRules.forEach((quoteResell) => {
          // On/Off
          this.selectedReferrers.push(`${quoteResell.referrerId}`);

          // Status to Send
          this.$set(
            this.referrerStatuses,
            quoteResell.referrerId,
            `${quoteResell.statusId}`
          );

          // Ship date
          this.hoursToSend = quoteResell.hoursToSend;
        });
      }
    },
    quoteResaleActivitySummary: {
      query: graphql.queries.getQuoteResaleActivitySummary,
      watchLoading(isLoading) {
        this.loadingQuoteResaleActivitySummary = isLoading;
      },
      result(result) {
        if (result.loading) return;
        this.quoteResaleActivitySummary =
          result.data.quoteResaleActivitySummary;
      }
    },
    getQuoteResaleActivities: {
      query: graphql.queries.getQuoteResaleActivities,
      watchLoading(isLoading) {
        this.loadingQuoteResaleActivities = isLoading;
      },
      result(result) {
        if (result.loading) return;
        this.quoteResaleActivities = result.data.quoteResaleActivities;
      }
    }
  },
  data() {
    return {
      referrers: [],
      statusOptions: [],
      loadingStatus: false,
      loadingQuoteResellRules: false,
      loadingQuoteResaleActivitySummary: false,
      loadingQuoteResaleActivities: false,
      selectedReferrers: [],
      referrerStatuses: {},
      hoursToSend: '',
      quoteResellRules: [],
      quoteResaleActivitySummary: { week: 0, month: 0, year: 0, all: 0 },
      quoteResaleActivities: []
    };
  },
  methods: {
    async handleSaveSchedule() {
      this.saving = true;

      const promiseSerial = this.leadScheduleByUsers.map(
        (leadSchedule) => async () => {
          const { userId, userName, ...leadScheduleInput } = leadSchedule;

          await this.$apollo.mutate({
            mutation: graphql.mutations.updateUserLeadSchedule,
            variables: {
              id: userId,
              input: sanitizeObject(leadScheduleInput)
            }
          });
        }
      );

      await runPromiseSerial(promiseSerial);

      this.saving = false;
    },
    handleChangeSelectReferrer(id, selected) {
      if (selected) {
        this.selectedReferrers = this.selectedReferrers.concat(id);
      } else {
        this.selectedReferrers = this.selectedReferrers.filter((q) => q !== id);
      }
    },
    handleChangeReferrerStatus(referrerId, value) {
      this.$set(this.referrerStatuses, referrerId, value.id);
    },
    async handleSave() {
      const payload = [];
      this.selectedReferrers.forEach((selectedReferrer) => {
        if (this.referrerStatuses[selectedReferrer]) {
          payload.push({
            referrerId: +selectedReferrer,
            statusId: +this.referrerStatuses[selectedReferrer],
            hoursToSend: this.hoursToSend
          });
        }
      });

      try {
        await this.$apollo.mutate({
          mutation: graphql.mutations.saveQuoteResellRules,
          variables: {
            payload: payload
          }
        });
        this.notifyPositive('Successfully Saved!');
      } catch (error) {
        this.notifyNegative(error.message);
      }
    }
  }
};
</script>

<style>
p {
  margin-bottom: 4px;
}
.wrapper {
  padding: 24px;
}
.title {
  font-size: 22px;
  font-weight: 600;
  line-height: 24px;
  margin: 0;
}
.content {
  padding: 8px;
  border: 2px solid #ddd;
  border-radius: 6px;
  margin-top: 20px;
  display: flex;
}
.sub-title {
  font-size: 16px;
  line-height: 20px;
  margin: 4;
}
.referrer-list {
  flex: 1;
}
.sent-history {
  flex: 1;
  margin: 8px 20px 0;
}
.config {
  flex: 1;
  margin: 8px 20px 0;
}
#referrer-tbl {
  width: 100%;
  border-spacing: 0;
}
#referrer-tbl > tbody > tr:nth-of-type(odd) > td:first-child {
  background-color: rgba(0, 0, 0, 0.05);
}
#referrer-tbl > tbody > tr > td:first-child {
  border-top: 1px solid #dee2e6;
  padding: 13px;
}
#referrer-tbl > thead > th {
  padding: 13px;
  text-align: center;
}
.save-wrapper {
  display: flex;
  height: calc(100% - 80px);
  align-items: flex-end;
  justify-content: flex-end;
}
</style>
